import { type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PAYPAL_MENTORIA_URL } from "@shared/../shared/constants";
import { CheckCircle2, ExternalLink, Users, Clock, Target, Video } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";

type Props = {
  /** The clickable element (button / link) that opens the dialog. Wrapped in DialogTrigger asChild. */
  children: ReactNode;
};

/**
 * Explains what the 1:1 mentoring session includes before sending the user
 * to PayPal. Wraps any trigger element so any existing "Agendar Mentoría" CTA
 * can be converted just by swapping the anchor for this component.
 */
export default function MentoringDialog({ children }: Props) {
  const { locale } = useLocale();
  const isEn = locale === "en";

  const included = isEn
    ? [
        { icon: Clock, text: "45 minutes 1:1 with Edmundo Treviño" },
        { icon: Target, text: "Personalized diagnosis of your business" },
        { icon: CheckCircle2, text: "Design of 1 to 3 extraordinary warranties tailored to you" },
        { icon: Video, text: "Optional recording so you can review the session later" },
      ]
    : [
        { icon: Clock, text: "45 minutos 1:1 con Edmundo Treviño" },
        { icon: Target, text: "Diagnóstico personalizado de tu negocio" },
        { icon: CheckCircle2, text: "Diseño de 1 a 3 garantías extraordinarias hechas para ti" },
        { icon: Video, text: "Grabación opcional para que puedas repasar la sesión" },
      ];

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-lg bg-card border-gold/30">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-2">
            <Users className="h-6 w-6 text-gold" />
          </div>
          <DialogTitle className="text-2xl font-serif text-center">
            {isEn ? "1:1 Mentoring with Edmundo" : "Mentoría 1:1 con Edmundo"}
          </DialogTitle>
          <DialogDescription className="text-center text-base text-foreground/80 leading-relaxed pt-2">
            {isEn ? (
              <>
                Would you like Edmundo to help you build marketing strategies you can
                implement in your business, stop competing on price and pursue more
                profitability through healthier sales for you and your customers?
                <br />
                <br />
                <strong className="text-gold">These 45 minutes could be the best investment.</strong>
              </>
            ) : (
              <>
                ¿Te gustaría que Edmundo te ayude a crear estrategias de marketing
                para implementarlas en tu negocio, dejes de competir en precio y
                busques más rentabilidad a través de ventas más saludables para ti y
                tus clientes?
                <br />
                <br />
                <strong className="text-gold">Estos 45 minutos podrían ser la mejor inversión.</strong>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 space-y-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground text-center font-semibold">
            {isEn ? "What you get" : "Qué incluye"}
          </p>
          <ul className="space-y-2.5">
            {included.map((item, i) => (
              <li key={i} className="flex gap-3 items-start">
                <item.icon className="h-4 w-4 text-gold shrink-0 mt-1" />
                <span className="text-sm text-foreground/90">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <DialogFooter className="flex-col sm:flex-col sm:space-x-0 gap-2">
          <a
            href={PAYPAL_MENTORIA_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full"
          >
            <Button className="w-full bg-gold text-navy-dark hover:bg-gold-light font-semibold">
              {isEn ? "Reserve my 45-min session" : "Reservar mi sesión de 45 min"}
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </a>
          <p className="text-[11px] text-center text-muted-foreground">
            {isEn
              ? "Secure payment via PayPal. Once complete, you'll receive an email to schedule your session."
              : "Pago seguro por PayPal. Al completarlo recibirás un correo para agendar tu sesión."}
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
